import {
  ALCHEMY_USE_INTEGRATION_HEADER,
  ALCHEMY_USE_INTEGRATION_HEADER_VALUE,
} from "@alch/dx-entities";
import useSearchParam, { SearchParam } from "@hooks/useSearchParam";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

export const INTEGRATION_MODE_STORAGE_KEY = "alchemy.useIntegrationMode";

/**
 * Integration mode is a special mode on staging that is used specifically
 * for running integration tests during CI before deploying new code to production.
 * It does not have any special semantic on any environment except staging.
 *
 * See [TDD](https://www.notion.so/alchemotion/Playwright-tests-in-CI-248069f20066805b93d0e466f7d2e2ff)
 */
export function isIntegrationModeEnabled(): boolean {
  return (
    window.localStorage.getItem(INTEGRATION_MODE_STORAGE_KEY) ===
    ALCHEMY_USE_INTEGRATION_HEADER_VALUE
  );
}

export function getIntegrationHeaders(): Record<string, string> | undefined {
  if (!isIntegrationModeEnabled()) {
    return undefined;
  }

  return {
    [ALCHEMY_USE_INTEGRATION_HEADER]: ALCHEMY_USE_INTEGRATION_HEADER_VALUE,
  };
}

export function useIntegrationModeStorage() {
  return useLocalStorage<boolean>(INTEGRATION_MODE_STORAGE_KEY, false);
}

export function useSyncIntegrationModeFromQuery(): void {
  const [, setIntegrationModeEnabled] = useIntegrationModeStorage();
  const integrationParam = useSearchParam(SearchParam.UseIntegration);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (integrationParam == null) {
      return;
    }

    setIntegrationModeEnabled(integrationParam.toLowerCase() === "true");
    const params = new URLSearchParams(location.search);
    params.delete(SearchParam.UseIntegration);
    history.replace({
      ...location,
      search: params.toString(),
    });
  }, [integrationParam, setIntegrationModeEnabled, history, location]);
}
